import React from 'react';
import { OptionProps } from 'react-select';
import styled from 'styled-components';
import { Box, BoxProps } from '../../../Box/Box';
import { Text } from '../../../Text/Text';
import { Toggle } from './Toggle';
import { Checkbox } from './Checkbox';
import { Icon } from '../../../Icon/Icon';
import PulseCheckLogo from '../../../../../PulseCheck/img/tile-logo.svg';

const getCustomIcon = (icon: string) => {
  if (icon === 'pulseCheck') {
    return (
      <PulseCheckLogo
        width={18}
        css={`
          color: ${({ theme }) => theme.colors.neutral['500']};
          margin-right: 6px;
        `}
      />
    );
  }

  return null;
};

interface LabelsProps {
  label: string;
  subLabel?: string | string[];
  noWrap?: boolean;
}

export const Labels = ({ label, subLabel, noWrap }: LabelsProps) => {
  const subLabels = typeof subLabel === 'string' ? [subLabel] : subLabel;

  return (
    <Box>
      <Text
        fontWeight={500}
        css={`
          white-space: ${noWrap ? 'nowrap' : 'unset'};
        `}
      >
        {label}
      </Text>

      {subLabels?.map(_subLabel => (
        <Text
          fontSize="small"
          color="neutral.500"
          css={`
            white-space: ${noWrap ? 'nowrap' : 'unset'};
          `}
        >
          {_subLabel}
        </Text>
      ))}
    </Box>
  );
};

interface LabelsWithRightLabelProps extends Omit<LabelsProps, 'noWrap'> {
  rightLabel: string;
}

export const LabelsWithRightLabel = ({
  label,
  subLabel,
  rightLabel,
}: LabelsWithRightLabelProps) => {
  return (
    <Box
      css={`
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 8px;
        width: 100%;
        align-items: center;
      `}
    >
      <Labels label={label} subLabel={subLabel} />

      <Text fontSize="small" color="neutral.500">
        {rightLabel}
      </Text>
    </Box>
  );
};

interface OptionWrapperProps extends BoxProps {
  isFocused: boolean;
  ref: any;
}

export const OptionWrapper = styled(Box)<OptionWrapperProps>(({ isFocused, theme }) => ({
  display: 'flex',
  cursor: 'pointer',
  fontWeight: theme.fontWeights['500'],
  color: theme.colors.neutral['700'],
  background: isFocused ? theme.colors.neutral['100'] : theme.colors.bg,
  height: 'auto',
}));

export function Option<Option, IsMulti extends boolean = false>({
  innerProps,
  isSelected,
  isFocused,
  selectProps,
  innerRef,
  data,
}: OptionProps<Option, IsMulti>) {
  const { variant, noWrap } = selectProps as any;

  const { label, subLabel, rightLabel, icon } = data as any;

  if (variant === 'toggle') {
    return (
      <OptionWrapper
        isFocused={isFocused}
        alignItems="center"
        justifyContent="space-between"
        p="8"
        m="8"
        ref={innerRef}
        {...innerProps}
      >
        <Labels label={label} subLabel={subLabel} noWrap={noWrap} />

        <Toggle isSelected={isSelected} />
      </OptionWrapper>
    );
  }

  if (variant === 'checkbox') {
    return (
      <OptionWrapper
        alignItems="center"
        isFocused={isFocused}
        p="8"
        m="8"
        ref={innerRef}
        {...innerProps}
      >
        <Box display="flex" flexDirection="row">
          <Checkbox isSelected={isSelected} mr="8" mt="2" />

          <Labels label={label} subLabel={subLabel} noWrap={noWrap} />
        </Box>
      </OptionWrapper>
    );
  }

  if (variant === 'icon') {
    const customIcon = getCustomIcon(icon);

    return (
      <OptionWrapper
        alignItems="center"
        isFocused={isFocused}
        p="8"
        m="8"
        ref={innerRef}
        {...innerProps}
      >
        <Box display="flex" flexDirection="row">
          {customIcon || <Icon className={icon} width={18} color="neutral.500" mt="4" mr={6} />}

          <Labels label={label} subLabel={subLabel} noWrap={noWrap} />
        </Box>
      </OptionWrapper>
    );
  }

  return (
    <OptionWrapper
      height="32px"
      px="16"
      py={6}
      my="4"
      alignItems="center"
      isFocused={isFocused}
      ref={innerRef}
      {...innerProps}
    >
      {rightLabel ? (
        <LabelsWithRightLabel label={label} subLabel={subLabel} rightLabel={rightLabel} />
      ) : (
        <Labels label={label} subLabel={subLabel} noWrap={noWrap} />
      )}
    </OptionWrapper>
  );
}
